import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useOptionalAuth } from './hooks/useOptionalAuth';
import AppShell from './components/AppShell';
import Dashboard from './pages/Dashboard';
import BillRuns from './pages/BillRuns';
import BillRunDetail from './pages/BillRunDetail';
import BillDetail from './pages/BillDetail';
import Tariffs from './pages/Tariffs';
import TariffManagement from './pages/TariffManagement';
import Pockets from './pages/Pockets';
import BuildingDetail from './pages/BuildingDetail';
import Tenants from './pages/Tenants';
import MeterConfig from './pages/MeterConfig';
import VirtualMeters from './pages/VirtualMeters';
import AuditLogs from './pages/AuditLogs';
import BillVerification from './pages/BillVerification';
import FieldTools from './pages/FieldTools';
import Callback from './pages/Callback';
import Login from './pages/Login';
import PrintBuildingReport from './pages/PrintBuildingReport';

// Code-split the Insights page so its echarts dependency (~1MB) only loads when
// the tab is opened — keeps the rest of the billing app lightweight.
const Insights = lazy(() => import('./pages/Insights'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const auth = useOptionalAuth();

  // No auth provider = no realm configured, redirect to login
  if (!auth) {
    return <Navigate to="/login" replace />;
  }

  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Callback />} />
      {/* Public, token-authed print target for the pdf-generator's headless
          browser. Deliberately outside ProtectedRoute — the signed URL is the
          authorization, not a Keycloak session. */}
      <Route path="/print/building-report" element={<PrintBuildingReport />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="insights"
          element={
            <Suspense fallback={<div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>}>
              <Insights />
            </Suspense>
          }
        />
        <Route path="bill-runs" element={<BillRuns />} />
        <Route path="bill-runs/:id" element={<BillRunDetail />} />
        <Route path="bills/:id" element={<BillDetail />} />
        <Route path="tariffs" element={<Tariffs />} />
        <Route path="tariff-management" element={<TariffManagement />} />
        <Route path="verification" element={<BillVerification />} />
        <Route path="pockets" element={<Pockets />} />
        <Route path="pockets/building/:buildingId" element={<BuildingDetail />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="meter-config" element={<MeterConfig />} />
        <Route path="virtual-meters" element={<VirtualMeters />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="field-tools/*" element={<FieldTools />} />
      </Route>
    </Routes>
  );
}
